import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import type {
  CardElementError,
  CardNewMandateError,
} from '@flywire/internal-react-elements';
import classNames from 'classnames';

import { Info } from 'components/Alert/Info/Info';
import { CreditCards } from 'components/CreditCards/CreditCards';
import { Heading } from 'components/Heading/Heading';
import { Spinner } from 'components/Spinner/Spinner';
import { FF_USER_WALLET } from 'constants/features';
import { useCreditCards } from 'hooks/useCreditCards';
import { usePeertransferFlags } from 'hooks/usePeertransferFlags/usePeertransferFlags';
import type { Price } from 'models';
import { getRecipientId } from 'selectors';
import { useTranslations } from 'utils';
import { formatToBrowserLocale } from 'utils/date/date';

import type { CreateRecurringBody } from '../../hooks/useCardPaymentContext';
import { useCardPaymentContext } from '../../hooks/useCardPaymentContext';
import { useRecurringOffer } from '../../hooks/useRecurringOffer';
import { useRecurringOfferPrice } from '../../hooks/useRecurringOfferPrice';
import { Steps } from '../../hooks/useSteps';

import './Pay.scss';

function Pay() {
  const { data: flags, isLoading: isLoadingFlags } = usePeertransferFlags(
    FF_USER_WALLET,
    { refetchOnWindowFocus: false, refetchInterval: false },
  );

  const i18n = useTranslations();
  const [isLoading, setIsLoading] = useState(false);
  const { offer, schedule } = useRecurringOffer();
  const { creditCards, hasCreditCards, isFecthingCreditCards } = useCreditCards(
    offer.cardSettings,
  );
  const { cardWizardNavigation, createRecurringReceivable, setErrorKey } =
    useCardPaymentContext();
  const { value, currency: currencyId } = offer.price as Price;

  const { payButtonLabel, currencyCode } = useRecurringOfferPrice(
    value,
    currencyId as string,
  );

  const recipientId = useSelector(getRecipientId);

  const createRecurring = async (body: CreateRecurringBody) => {
    setIsLoading(true);

    try {
      await createRecurringReceivable(body);

      setIsLoading(false);
      cardWizardNavigation.go(Steps.Success);
    } catch (_error) {
      setIsLoading(false);
      cardWizardNavigation.go(Steps.Error);
    }
  };

  const onSuccess = (
    data: { confirm: { url: string; method: string } },
    sessionId: string,
  ) => {
    createRecurring({
      apiConfirmationURL: data?.confirm?.url,
      sessionId: sessionId,
    });
  };

  const onError = async (error?: CardNewMandateError | CardElementError) => {
    setIsLoading(false);

    if (error?.type !== 'invalid_cvv' && error?.type !== 'invalid_card_info') {
      if (!error || error.type === 'not_found') {
        setErrorKey('notifications.internal_server_failure');
      }
      cardWizardNavigation.go(Steps.Error);
    }
  };

  const enableUserWallet = !!flags?.[FF_USER_WALLET];

  const showSpinner = isLoading || isLoadingFlags || isFecthingCreditCards;

  return (
    <div className="PayRecurring">
      {showSpinner ? (
        <Spinner className="PayRecurring-spinner" />
      ) : (
        <>
          {schedule !== 'pay_now' && (
            <Info className={classNames(!hasCreditCards && 'Info-NoCards')}>
              <Info.FilledIcon />
              <span className="Info-text">
                {i18n.t('cardPayment.payLaterInfo', {
                  date: formatToBrowserLocale(offer.installments[0].date),
                })}
              </span>
            </Info>
          )}

          <Heading as="h2" size="large" className="fontWeight-semiBold">
            {i18n.t('cardPayment.payer.title')}
          </Heading>
          {/* TODO move some props (currency, recipient, button props, onSuccess, onError) to the parent to have it in one place */}
          <CreditCards cards={creditCards}>
            {enableUserWallet && hasCreditCards && (
              <CreditCards.CardList
                currency={currencyCode}
                onErrorCVV={onError}
                onSuccessCVV={onSuccess}
                recipientId={recipientId}
              />
            )}
            <CreditCards.AddCardElement
              buttonLabel={payButtonLabel}
              currency={currencyCode}
              onSuccess={onSuccess}
              recipientId={recipientId}
              onError={onError}
              cardSettings={offer.cardSettings}
              sessionType="card_tokenization_for_installment"
            />
            <CreditCards.Actions>
              <CreditCards.PayButton
                isLoading={isLoading}
                payButtonLabel={payButtonLabel as string}
              />
            </CreditCards.Actions>
            <CreditCards.Disclaimer />
          </CreditCards>
        </>
      )}
    </div>
  );
}

export { Pay };
